import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
} from '../../api/authenticatedFetch';

const BASE = '/api/v1';

// --- Roles (enriched catalog: purpose, risk, capabilities, assigned_user_count, conflicts) ---

/**
 * @param {{ includeHidden?: boolean }} [params]
 */
export const listRolesEnriched = async (params = {}) => {
  const query = {};
  if (params.includeHidden) query.include_hidden = 1;
  const res = await authenticatedGet(`${BASE}/roles`, query);
  return res?.roles ?? [];
};

// --- Catalog grouping (Roles & Access tabs: Organisation / Templates / Platform / Archived) ---

const ROLE_TYPE_BUCKETS = ['organisation', 'template', 'platform'];

/**
 * Groups the enriched role catalog (see `listRolesEnriched`) by `role.type`.
 * Any role whose `type` isn't one of the three known buckets falls back to
 * "organisation" so nothing silently disappears from the catalog. An
 * organisation role whose governance `status` is 'retired' (Sub-project E2
 * Task 7's lifecycle: draft -> pending_approval -> active -> retired) is
 * rerouted into "archived" instead — the Organisation tab only shows a
 * role's active lifecycle, the Archived tab is its retirement record.
 * Template/platform roles don't carry a governance lifecycle, so their
 * bucket is unaffected by `status`.
 *
 * @param {Array<{ type?: string, status?: string }>} [roles]
 * @returns {{ organisation: Array, template: Array, platform: Array, archived: Array }}
 */
export function groupRolesByType(roles = []) {
  const groups = { organisation: [], template: [], platform: [], archived: [] };
  for (const role of roles) {
    const bucket = ROLE_TYPE_BUCKETS.includes(role.type) ? role.type : 'organisation';
    if (bucket === 'organisation' && role.status === 'retired') {
      groups.archived.push(role);
    } else {
      groups[bucket].push(role);
    }
  }
  return groups;
}

// --- Capabilities catalog (code/name/description/group/risk/is_scoped) ---

export const listCapabilities = async () => {
  const res = await authenticatedGet(`${BASE}/capabilities`);
  return res?.capabilities ?? [];
};

// --- Custom role CRUD (system roles are immutable — the backend rejects
// PUT/DELETE on them with 409 ROLE_IMMUTABLE) ---

/**
 * @param {{ code: string, name: string, purpose?: string, cloned_from?: string, capabilities?: string[] }} body
 *   `capabilities` may be omitted when `cloned_from` is set — the backend
 *   copies the cloned-from role's capabilities in that case.
 */
export const createRole = async (body) => {
  const res = await authenticatedPost(`${BASE}/roles`, body);
  return res?.role ?? res;
};

/**
 * @param {number|string} id - the role's numeric id (not its `code`)
 * @param {{ name: string, purpose?: string, capabilities: string[] }} body
 */
export const updateRole = async (id, body) => {
  const res = await authenticatedPut(`${BASE}/roles/${id}`, body);
  return res?.role ?? res;
};

/**
 * @param {number|string} id - the role's numeric id (not its `code`)
 */
export const deleteRole = async (id) => {
  return authenticatedDelete(`${BASE}/roles/${id}`);
};

// --- Governed org-role creation, control-check, lifecycle (Sub-project E2) ---

/**
 * Dry-runs the control-check engine (internal/rolecontrol) against a
 * PROPOSED capability set — doesn't persist anything. Used by
 * OrgRoleWizard's control-check step; the same rule set re-runs
 * server-side (and authoritatively) when the role is actually created at
 * status='active' or transitioned ->active (see createOrgRole).
 *
 * @param {{ caps: string[], hasApprovalAuthority?: boolean, isScoped?: boolean, targetUserIds?: number[] }} body
 * @returns {Promise<Array<{ code: string, severity: 'critical'|'warning'|'info', detail: string, subjects: string[] }>>}
 */
export const controlCheck = async (body) => {
  const res = await authenticatedPost(`${BASE}/roles/control-check`, body);
  return res?.findings ?? [];
};

/**
 * Creates a GOVERNED organisation role (Sub-project E2, Task 4's extended
 * POST /api/v1/roles): owner/review-cadence/sensitivity/default-authority in
 * one call, at the requested lifecycle status. A status:'active' request
 * that fails the server-side activation control-gate on a CRITICAL finding
 * (and carries no override_reason) rejects with 409
 * ROLE_CONTROL_CHECK_FAILED — the caller (OrgRoleWizard) reads
 * `err.serverBody.error.findings` to re-render the control-check step.
 *
 * @param {{
 *   code: string, name: string, purpose?: string, cloned_from?: string,
 *   capabilities: string[], status: 'draft'|'active',
 *   owner_user_id?: number, review_frequency_days?: number|null,
 *   includes_sensitive?: boolean, is_scoped?: boolean,
 *   default_authority?: Array<{ object_type: string, max_amount_cents: number }>,
 *   override_reason?: string,
 * }} body
 */
export const createOrgRole = async (body) => {
  const res = await authenticatedPost(`${BASE}/roles`, body);
  return res?.role ?? res;
};

/**
 * Drives an organisation role's governance lifecycle
 * (draft -> pending_approval -> active -> retired). A ->active request is
 * subject to the same activation control-gate as createOrgRole — pass
 * `overrideReason` to push through a critical finding.
 *
 * @param {number|string} id
 * @param {'draft'|'pending_approval'|'active'|'retired'} to
 * @param {{ reason?: string, overrideReason?: string }} [opts]
 */
export const transitionRole = async (id, to, opts = {}) => {
  const body = { to };
  if (opts.reason) body.reason = opts.reason;
  if (opts.overrideReason) body.override_reason = opts.overrideReason;
  const res = await authenticatedPost(`${BASE}/roles/${id}/transition`, body);
  return res?.role ?? res;
};

// --- Compare-with-template + selective adoption (Sub-project E3, Task 4) ---

/**
 * The diff between an organisation role's template-cloning baseline and that
 * template's current latest role_template_version (internal/users.TemplateDiff
 * -- cmd/api/template_diff_handlers.go's RoleTemplateDiff writes this
 * unwrapped, no envelope key, so the raw fetch response IS the diff object;
 * there's nothing to unwrap beyond that).
 *
 * @param {number|string} id - the ORGANISATION role's numeric id
 * @returns {Promise<{ roleId: number, templateCode: string, baseVersion: number,
 *   latestVersion: number, added: string[], removed: string[],
 *   currentRoleCaps: string[], behind: boolean }>}
 */
export const getTemplateDiff = async (id) => {
  return authenticatedGet(`${BASE}/roles/${id}/template-diff`);
};

// --- Lifecycle-history timeline (role lifecycle-history) ---

/**
 * The full role_lifecycle_event audit trail for an organisation role,
 * newest first (internal/users.RoleLifecycleEvent via
 * GET /api/v1/roles/{id}/lifecycle) -- backs the role-detail "Lifecycle
 * history" timeline. Only organisation roles have one; platform/template
 * roles reject with 409 ROLE_IMMUTABLE, so callers should gate the fetch on
 * `role.type === 'organisation'` (see useRoleLifecycle below).
 *
 * @param {number|string} id - the ORGANISATION role's numeric id
 * @returns {Promise<Array<{ id: number, fromStatus: string, toStatus: string,
 *   actorUserId: number|null, actorEmail: string, reason: string,
 *   overrideFindings: { override_reason?: string, critical_findings?: string[] }|null,
 *   occurredAt: string }>>}
 */
export const getRoleLifecycle = async (id) => {
  const res = await authenticatedGet(`${BASE}/roles/${id}/lifecycle`);
  return res?.events ?? [];
};

/**
 * Tenant-wide feed of organisation roles whose cloning template has drifted
 * past the version they were created/adopted at -- the "review template
 * updates" list. Cheaper than calling getTemplateDiff per row: one call
 * covers every behind role in the tenant.
 *
 * @returns {Promise<Array<{ roleId: number, roleName: string, templateCode: string,
 *   addedCount: number, removedCount: number }>>}
 */
export const getTemplateUpdates = async () => {
  const res = await authenticatedGet(`${BASE}/roles/template-updates`);
  return res?.roles ?? [];
};

/**
 * Selectively adopts a subset (or the full set) of a template's offered
 * Added/Removed capabilities (from getTemplateDiff) into an organisation
 * role's own capability set. `add`/`remove` must each be a subset of that
 * role's currently-offered diff -- the backend rejects anything else with
 * 400 CAP_NOT_OFFERED. A 409 ROLE_CONTROL_CHECK_FAILED (the resulting
 * capability set trips a CRITICAL rolecontrol finding) carries
 * `err.serverBody.error.findings`; re-submit with `override_reason` set to
 * push through, mirroring OrgRoleWizard's activation-gate override.
 *
 * @param {number|string} id - the organisation role's numeric id
 * @param {{ add?: string[], remove?: string[], reason?: string, override_reason?: string }} body
 * @returns {Promise<{ roleId: number, adoptedFully: boolean }>}
 */
export const adoptTemplateUpdates = async (id, body) => {
  const res = await authenticatedPost(`${BASE}/roles/${id}/adopt-template-updates`, body);
  return res?.result ?? res;
};

// --- Guided finance starter set (Sub-project E4, Task 5) ---

/**
 * Creates (or, for a template already derived into this tenant, resolves)
 * a curated set of DRAFT organisation roles for a named starter set —
 * currently only `"finance"` — so a tenant admin doesn't have to hand-clone
 * each recommended template one at a time. Every returned role is a
 * control-checked DRAFT with no users assigned; idempotent per template
 * (a template already derived into an org role for this tenant comes back
 * with `existing: true` instead of being re-created).
 *
 * @param {{ set: 'finance' }} body
 * @returns {Promise<Array<{ id: number, name: string, templateCode: string,
 *   existing: boolean, findings: Array<{ code: string, severity: 'critical'|'warning'|'info', detail: string }> }>>}
 */
export const starterSet = async (body) => {
  const res = await authenticatedPost(`${BASE}/roles/starter-set`, body);
  return res?.roles ?? [];
};

// --- Platform temporary elevation (Sub-project E4, Task 6) ---

/**
 * Grants a PLATFORM role (tenant_owner/security_admin) to a user — either
 * permanently (`expires_at` omitted) or for a time-boxed window
 * (`expires_at` set, RFC3339 — the scheduled sweep, backend Task 3, later
 * auto-revokes it). Unlike updateUser's RoleCodes replace, this is a
 * targeted ADD — the user's other role assignments are untouched.
 *
 * A tenant that requires approval for platform assignments (appparams
 * platform.assignment_requires_approval) rejects an immediate grant with
 * 409 `PLATFORM_APPROVAL_REQUIRED`; granting a non-platform role code
 * rejects with 400 `NOT_PLATFORM_ROLE`. Both surface via
 * `err.serverBody.error.code` for the caller to render a friendly message.
 *
 * @param {number|string} userId
 * @param {{ role_code: string, expires_at?: string, reason: string }} body
 */
export const grantPlatformRole = async (userId, body) => {
  const res = await authenticatedPost(`${BASE}/users/${userId}/platform-grant`, body);
  return res?.platform_grant ?? res;
};

/**
 * Early-revokes a platform-role assignment (the optional DELETE counterpart
 * to grantPlatformRole) — idempotent, revoking a role the user doesn't hold
 * is not an error.
 *
 * @param {number|string} userId
 * @param {string} roleCode
 */
export const revokePlatformRole = async (userId, roleCode) => {
  return authenticatedDelete(`${BASE}/users/${userId}/platform-grant/${roleCode}`);
};

// --- Platform-grant approval workflow (Platform Access Hardening) ---
// When a tenant sets platform.assignment_requires_approval, a platform grant
// creates a PENDING platform_grant_request instead of granting immediately;
// a DIFFERENT admin (hard SoD: requester != approver) approves or rejects it.

// Lists the tenant's pending platform-grant requests (the approver worklist).
export const listPlatformGrantRequests = async () => {
  const res = await authenticatedGet(`${BASE}/platform-grant-requests`);
  return res?.requests ?? [];
};

// Approves a pending request → materializes the platform grant. 403
// SELF_APPROVAL_FORBIDDEN when the approver is the requester.
export const approvePlatformGrantRequest = async (id, body = {}) => {
  return authenticatedPost(`${BASE}/platform-grant-requests/${id}/approve`, body);
};

// Rejects a pending request (no grant made).
export const rejectPlatformGrantRequest = async (id, body = {}) => {
  return authenticatedPost(`${BASE}/platform-grant-requests/${id}/reject`, body);
};

// --- react-query hooks ---

/**
 * The role catalog for the Roles & Access page. Named distinctly from
 * `src/authz/useCapabilities` (that hook returns the *viewer's own* granted
 * capability codes; this one fetches the tenant's role catalog).
 *
 * @param {{ includeHidden?: boolean }} [params]
 */
export function useRoles(params = {}) {
  const includeHidden = !!params.includeHidden;
  return useQuery(['roles', { includeHidden }], () => listRolesEnriched({ includeHidden }), {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Named `useCapabilityCatalog` (not `useCapabilities`) to avoid clashing with
// `src/authz/useCapabilities` — that hook is about the current user's grants,
// this one is the full capability catalog (used to resolve capability
// code -> name/group for the role cards).
export function useCapabilityCatalog() {
  return useQuery(['capability-catalog'], listCapabilities, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Thin useMutation wrappers around the three E2 functions above — OrgRoleWizard
// (src/views/roles/OrgRoleWizard/) is the primary caller; `options` passes
// through untouched (onSuccess/onError/onSettled), same shape react-query's
// own useMutation takes, so callers configure per-use-site behavior (toast,
// cache invalidation, navigation) exactly as they would calling useMutation
// directly.
export function useControlCheckMutation(options) {
  return useMutation((body) => controlCheck(body), options);
}

export function useCreateOrgRoleMutation(options) {
  return useMutation((body) => createOrgRole(body), options);
}

export function useTransitionRoleMutation(options) {
  return useMutation(({ id, to, ...opts }) => transitionRole(id, to, opts), options);
}

// --- Compare-with-template + selective adoption hooks (Sub-project E3, Task 4) ---

/**
 * A single organisation role's template diff (TemplateDiffModal). Disabled
 * until an id is available (the modal only mounts meaningfully once a role
 * is chosen) or the caller explicitly opts out via `options.enabled`.
 *
 * @param {number|string} [id]
 * @param {{ enabled?: boolean }} [options]
 */
export function useTemplateDiff(id, options = {}) {
  const enabled = !!id && options.enabled !== false;
  return useQuery(['role-template-diff', id], () => getTemplateDiff(id), {
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * A single organisation role's lifecycle-history timeline (RoleDetailModal).
 * Disabled until an id is available, or the caller opts out via
 * `options.enabled` -- platform/template roles have no governance lifecycle,
 * so the modal only enables this for `role.type === 'organisation'`.
 *
 * @param {number|string} [id]
 * @param {{ enabled?: boolean }} [options]
 */
export function useRoleLifecycle(id, options = {}) {
  const enabled = !!id && options.enabled !== false;
  return useQuery(['role-lifecycle', id], () => getRoleLifecycle(id), {
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Tenant-wide "review template updates" feed — drives both the Organisation
// table's "Newer template · +N/−M" chip and the Review template updates
// section (RolesCatalogPage), keyed by roleId so a row lookup is O(1)
// instead of a per-row template-diff call.
export function useTemplateUpdates() {
  return useQuery(['role-template-updates'], getTemplateUpdates, {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Adopts a chosen subset of a template's offered updates into an
 * organisation role. Bakes in the cache invalidation every caller needs
 * (the role's own catalog row, its diff, and the tenant-wide behind-roles
 * feed all move once adoption succeeds) so call sites only need to layer on
 * their own onSuccess/onError (toast, closing the modal, etc.) via `options`
 * — same passthrough convention as the other mutation hooks above.
 *
 * @param {{ onSuccess?: Function, onError?: Function, onSettled?: Function }} [options]
 */
export function useAdoptTemplateUpdatesMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation(({ id, add, remove, override_reason: overrideReason }) =>
    adoptTemplateUpdates(id, { add, remove, override_reason: overrideReason }), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['role-template-diff'] });
      queryClient.invalidateQueries({ queryKey: ['role-template-updates'] });
      options.onSuccess?.(...args);
    },
  });
}

// --- Guided finance starter-set hook (Sub-project E4, Task 5) ---

/**
 * Wraps `starterSet` — invalidates the role catalog on success (the newly
 * drafted roles, or the already-`existing` ones resolved back, both need to
 * show up in the Organisation tab) before layering on the caller's own
 * `options` (StarterSetPanel keeps the mutation's own `data`/`isSuccess` to
 * switch from the confirm step to the results list, so it doesn't need an
 * onSuccess of its own here — only onError to surface a failure toast).
 *
 * @param {{ onSuccess?: Function, onError?: Function, onSettled?: Function }} [options]
 */
export function useStarterSetMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation((body) => starterSet(body), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      options.onSuccess?.(...args);
    },
  });
}

// --- Platform temporary elevation hooks (Sub-project E4, Task 6) ---

/**
 * Grants a platform role (PlatformAssignmentsModal). Invalidates both the
 * access-review query (its `platformHolders` list is the source the modal
 * and the access-review Platform panel both read the new grant from) and
 * the role catalog (a role's `assigned_user_count` moves).
 *
 * @param {{ onSuccess?: Function, onError?: Function, onSettled?: Function }} [options]
 */
export function useGrantPlatformRoleMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation(({ userId, body }) => grantPlatformRole(userId, body), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['access-review'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      options.onSuccess?.(...args);
    },
  });
}

/**
 * Revokes a platform role early (PlatformAssignmentsModal's per-holder
 * Revoke action). Same cache invalidation as the grant mutation above.
 *
 * @param {{ onSuccess?: Function, onError?: Function, onSettled?: Function }} [options]
 */
export function useRevokePlatformRoleMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation(({ userId, roleCode }) => revokePlatformRole(userId, roleCode), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['access-review'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      options.onSuccess?.(...args);
    },
  });
}

/**
 * The approver's pending platform-grant-request worklist. Enable only while
 * the managing dialog is open (the endpoint is gated roles.assign).
 */
export function usePlatformGrantRequests(options = {}) {
  return useQuery(['platform-grant-requests'], listPlatformGrantRequests, {
    refetchOnWindowFocus: false,
    ...options,
  });
}

/**
 * Approves/rejects a pending platform-grant request. On a successful approve
 * the grant is materialized server-side, so we invalidate the request list,
 * the access-review platformHolders, and the role catalog.
 */
function usePlatformGrantDecisionMutation(decide, options = {}) {
  const queryClient = useQueryClient();
  return useMutation(({ id, body }) => decide(id, body), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['platform-grant-requests'] });
      queryClient.invalidateQueries({ queryKey: ['access-review'] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      options.onSuccess?.(...args);
    },
  });
}

export function useApprovePlatformGrantRequestMutation(options = {}) {
  return usePlatformGrantDecisionMutation(approvePlatformGrantRequest, options);
}

export function useRejectPlatformGrantRequestMutation(options = {}) {
  return usePlatformGrantDecisionMutation(rejectPlatformGrantRequest, options);
}
